import FloatingBorrowIcon from "@/assets/icons/floating-borrow.svg"
import FloatingSupplyIcon from "@/assets/icons/floating-supply.svg"
import FloatingValueLockedIcon from "@/assets/icons/floating-value-locked.svg"
import RelendLogo from "@/assets/icons/relend-light.svg"
import DogCuteOverview from "@/assets/images/dog-cute-overview.png"
import { poolsAtom } from "@/atoms/pools"
import { networkAtom } from "@/atoms/settings"
import { solendUSDSObligationsStatsAtom } from "@/atoms/solendObligations"
import { selectedSolendPoolAtom } from "@/atoms/solendPools"
import useRestakingStats from "@/hooks/useRestakingStats"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { SKELETON_VALUE } from "@/utils/constants"
import { getTotalValues } from "@/utils/dashboard/getTotalValues"
import { formatUsd } from "@/utils/numberFormatter"
import { Stack, Typography } from "@mui/material"
import { BlockchainNetwork, USDS } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import { useAtomValue } from "jotai"
import Image from "next/image"
import { useMemo } from "react"

const MarketOverviewItem = ({ icon, title, value }: { icon: any; title: string; value: string }) => {
  return (
    <Stack direction='row' alignItems='center' gap={2} className='z-20'>
      <Image src={icon} alt='icon' width={40} height={40} />
      <Stack gap={0}>
        <Typography className='text-neutral-600 text-xs sm:text-sm'>{title}</Typography>
        <Typography className='text-neutral-1000 text-lg sm:text-xl font-bold'>{value}</Typography>
      </Stack>
    </Stack>
  )
}
const MarketOverview = () => {
  const trans = useTrans()
  const network = useAtomValue(networkAtom)
  const { data: dataStacking } = useRestakingStats(network === BlockchainNetwork.RENEC)
  const solendUSDSObligationsStats = useAtomValue(solendUSDSObligationsStatsAtom)
  const { isSolanaChain } = useWalletConnect()
  const selectedSolendPool = useAtomValue(selectedSolendPoolAtom)

  const poolValues = useAtomValue(poolsAtom)

  const [totalSupplyUsd, totalBorrowUsd, totalMarket] = useMemo(() => {
    const totalValues = poolValues ? getTotalValues(Object.values(poolValues)[0]) : undefined

    if (!totalValues) {
      return ["0", "0", "0"]
    }

    const { totalSupplyUsd, totalBorrowUsd, totalMarket } = totalValues

    let totalStackedUsd = 0
    if (dataStacking && dataStacking.relTVLUsd) {
      totalStackedUsd = dataStacking.relTVLUsd
    }

    let totalSuppliedUSDS = new BigNumber(0)
    if (isSolanaChain) {
      totalSuppliedUSDS = new BigNumber(solendUSDSObligationsStats?.totalSupplyAmount ?? 0)
      const usdsReserve = selectedSolendPool?.reserves.find((r) => r.symbol === USDS)
      if (usdsReserve) {
        totalSuppliedUSDS = totalSuppliedUSDS.multipliedBy(usdsReserve.price)
      }
    } else {
      totalSuppliedUSDS = new BigNumber(0)
    }
    const totalValueMarket = new BigNumber(totalMarket).plus(totalStackedUsd).plus(totalSuppliedUSDS).toString()

    const totalSupplyUsdWithUSDS = new BigNumber(totalSupplyUsd).plus(totalSuppliedUSDS).toString()
    return [totalSupplyUsdWithUSDS, totalBorrowUsd, totalValueMarket]
  }, [
    poolValues,
    dataStacking,
    isSolanaChain,
    solendUSDSObligationsStats?.totalSupplyAmount,
    selectedSolendPool?.reserves,
  ])

  const items = useMemo(() => {
    return [
      {
        icon: FloatingSupplyIcon,
        title: trans.dashboard.hero.total_supply,
        value: totalSupplyUsd === "0" ? SKELETON_VALUE : formatUsd(totalSupplyUsd),
      },
      {
        icon: FloatingBorrowIcon,
        title: trans.dashboard.hero.total_borrow,
        value: totalBorrowUsd === "0" ? SKELETON_VALUE : formatUsd(totalBorrowUsd),
      },
      {
        icon: FloatingValueLockedIcon,
        title: trans.dashboard.hero.total_market,
        value: totalMarket === "0" ? SKELETON_VALUE : formatUsd(totalMarket),
      },
    ]
  }, [trans, totalSupplyUsd, totalBorrowUsd, totalMarket])

  return (
    <Stack
      className='relative py-4 px-5 bg-white rounded-lg border border-solid border-secondary min-w-80 min-h-[21rem] sm:min-h-[625px]'
      gap={4}
      data-testid='market-overview'
    >
      <Typography className='text-neutral-1000 font-bold text-xl'>{trans.dashboard.hero.market_overview}</Typography>

      {items.map((item) => (
        <MarketOverviewItem key={item.title} {...item} />
      ))}

      <Image
        className='absolute bottom-6 right-0 w-24 h-24 sm:right-auto sm:left-10 sm:bottom-24 sm:h-40 sm:w-40 z-10'
        src={DogCuteOverview}
        alt='hero-stat-icon'
      />
      <Image
        className='absolute bottom-0 right-0 z-0 sm:h-52 sm:w-52'
        src={RelendLogo}
        alt='relend'
        width={75}
        height={79}
      />
    </Stack>
  )
}

export default MarketOverview
